import React, { useState, useEffect } from 'react';
import MainLayout from '../layout/MainLayout';
import planApi from '../../services/planApi';
import config from '../../config';
import { Check, X, Video, MapPin } from 'lucide-react';
import './admin-scheduling.scss';

interface Sess {
  id: number;
  title: string;
  scheduled_date: string;
  status: string;
  duration_minutes: number | null;
}
interface Cell { idx: number; ym: string; status: 'done' | 'missed' | 'upcoming'; sessions: Sess[]; }
interface Dupla {
  relationship_id: number;
  mentee: string;
  mentor: string;
  mentee_image: string | null;
  mentor_image: string | null;
  cells: Cell[];
}
interface Month { idx: number; ym: string; label: string; }
interface SchedData { program_start: string; current_ym: string; months: Month[]; duplas: Dupla[]; }

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
  return `${baseUrl}/${imagePath}`;
};

const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

const STATUS_LBL: Record<string, string> = {
  completed: 'Efectiva', scheduled: 'Agendada', pending: 'Agendada', no_show: 'No asistió',
};

const Avatars: React.FC<{ dupla: Dupla }> = ({ dupla }) => {
  const menteeImg = getImageUrl(dupla.mentee_image);
  const mentorImg = getImageUrl(dupla.mentor_image);
  return (
    <div className="ag-avatars">
      <span className="ag-avatar ag-avatar--back">
        {mentorImg ? <img src={mentorImg} alt={dupla.mentor} /> : <span className="ag-avatar__ini">{initials(dupla.mentor)}</span>}
      </span>
      <span className="ag-avatar ag-avatar--front">
        {menteeImg ? <img src={menteeImg} alt={dupla.mentee} /> : <span className="ag-avatar__ini">{initials(dupla.mentee)}</span>}
      </span>
    </div>
  );
};

const eyebrowDate = () => {
  const d = new Date();
  const dia = d.toLocaleDateString('es', { weekday: 'long' });
  const num = d.getDate();
  const mes = d.toLocaleDateString('es', { month: 'short' }).replace('.', '');
  return `${dia} ${num} de ${mes} · Administrador`.toUpperCase();
};

const fmtDateTime = (sql: string) => {
  const d = new Date(String(sql).replace(' ', 'T'));
  if (isNaN(d.getTime())) return sql;
  return d.toLocaleString('es', { dateStyle: 'long', timeStyle: 'short' });
};

const AdminScheduling: React.FC = () => {
  const [data, setData] = useState<SchedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState<number>(0); // 0 = todos
  const [detail, setDetail] = useState<{ dupla: Dupla; cell: Cell } | null>(null);

  useEffect(() => {
    planApi.getScheduling()
      .then((res: SchedData) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const visibleMonths = (months: Month[]) =>
    monthFilter === 0 ? months : months.filter(m => m.idx === monthFilter);

  const openCell = (dupla: Dupla, cell: Cell) => {
    if (cell.status !== 'done') return;
    setDetail({ dupla, cell });
  };

  return (
    <MainLayout>
      <div className="admin-scheduling">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{eyebrowDate()}</div>
            <h1 className="heading-1">Agendamiento de <em>sesiones</em></h1>
            <p className="ag-intro">Cada dupla debe sostener al menos 1 sesión al mes. Haz clic en una sesión para ver su detalle.</p>
            <p className="ag-intro">
              Meta: <strong>al menos 1 sesión por dupla al mes</strong>. Verde = hubo sesión (clic para ver el detalle).
              Rojo = mes sin sesión. Gris = aún no llega.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="ag-loading">Cargando agendamiento…</div>
        ) : !data ? (
          <div className="ag-loading">No se pudo cargar el agendamiento.</div>
        ) : (
          <>
            <div className="ag-toolbar">
              <select className="ag-select" value={monthFilter} onChange={e => setMonthFilter(Number(e.target.value))}>
                <option value={0}>Todos los meses</option>
                {data.months.map(m => (
                  <option key={m.idx} value={m.idx}>M{m.idx} · {m.label}</option>
                ))}
              </select>
            </div>

            <div className="ag-card">
              <div className="ag-scroll">
                {data.duplas.length === 0 ? (
                  <p className="ag-empty">No hay duplas activas.</p>
                ) : data.duplas.map(d => (
                  <div className="ag-row" key={d.relationship_id}>
                    <div className="ag-row__dupla">
                      <Avatars dupla={d} />
                      <div className="ag-row__names">
                        <span className="ag-row__mentee">{d.mentee}</span>
                        <span className="ag-row__mentor">↪ {d.mentor}</span>
                      </div>
                    </div>
                    <div className="ag-cells">
                      {visibleMonths(data.months).map(m => {
                        const cell = d.cells.find(c => c.idx === m.idx)!;
                        return (
                          <button
                            key={m.idx}
                            className={`ag-cell ag-cell--${cell.status}`}
                            onClick={() => openCell(d, cell)}
                            disabled={cell.status !== 'done'}
                            title={`${m.label}: ${cell.status === 'done' ? 'con sesión' : cell.status === 'missed' ? 'sin sesión' : 'aún no llega'}`}
                          >
                            <span className="ag-cell__mark">
                              {cell.status === 'done' ? <Check size={16} /> : cell.status === 'missed' ? '—' : '·'}
                            </span>
                            <span className="ag-cell__lbl">M{m.idx}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {detail && (
          <div className="ag-modal-overlay">
            <div className="ag-modal" role="dialog" aria-modal="true">
              <button className="ag-modal__close" onClick={() => setDetail(null)} aria-label="Cerrar"><X size={18} /></button>
              <div className="ag-modal__eyebrow">
                {data?.months.find(m => m.idx === detail.cell.idx)?.label} · M{detail.cell.idx}
              </div>
              <h3 className="ag-modal__title">{detail.dupla.mentee} <span>↔ {detail.dupla.mentor}</span></h3>
              <div className="ag-modal__list">
                {detail.cell.sessions.map(s => (
                  <div className="ag-sess" key={s.id}>
                    <div className="ag-sess__head">
                      <span className="ag-sess__title">{s.title || 'Sesión'}</span>
                      <span className={`ag-sess__pill ag-sess__pill--${s.status}`}>{STATUS_LBL[s.status] || s.status}</span>
                    </div>
                    <div className="ag-sess__meta">
                      {s.title?.toLowerCase().includes('presencial') ? <MapPin size={13} /> : <Video size={13} />}
                      <span>{fmtDateTime(s.scheduled_date)}</span>
                      {s.duration_minutes ? <span>· {s.duration_minutes} min</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminScheduling;
